import React from 'react';

import { PedigreeListDto } from "@common/dto/pedigree/list-dto";
import { GetPedigreesRequestParams } from "@modules/invite-status/services/pedigrees";
import { BodyNameCell } from '@common/components/body-name-cell';
import { NewTableColumn, NewTableColumnAction, NewTableColumnPredefinedActionType, NewTableGetId, NewTableHeaderAction, NewTableHeaderPredefinedActionType, NewTableLoadMetadata, NewTableSortingDirection } from '@common/components/new-table';
import { useTableController } from '@common/hooks/table';
import { AdminSmartListFunctionType } from '@common/dto/admin-function/type';
import { I18n } from 'react-redux-i18n';
import { ColumnListDto } from '@common/dto/column/list-dto';
import { getTestAttributes } from '@common/utils/tests';
import { AdminSmartListFunctionGetDto } from '@common/dto/admin-function/get';
import { generateId } from '@common/utils/common';
import { useTableFiltersController } from '@common/hooks/table/filters';
import { HttpErrorDto } from '@common/dto/http';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { FieldListDto } from '@common/dto/field/list-dto';
import { ColumnSaveDto } from '@common/dto/column/save-dto';

export type PedigreeChooserProps = {
  getAdminSmartListFunctions: () => Promise<AdminSmartListFunctionGetDto>;
  savePedigreesColumns: (columns: Array<ColumnSaveDto>) => Promise<void>;
  getPedigreesColumns: () => Promise<Array<ColumnListDto>>;
  getFolderFields: (params: {
    searchQuery?: string | undefined;
    folderId?: number | undefined;
    system: boolean;
    contextId: number;
  }) => Promise<Array<FieldListDto>>;
  getFolders: (params: {
    folderTypeId: number;
    setUp: {
      rootFolderName: string;
      rootFolderImgUrl: string;
    };
  }) => Promise<Array<FolderPrimaryListDto>>;
  showErrorMessage: (msg: string) => void;
  getFilters: () => Promise<Record<string, string>>;
  getPedigrees: (params: GetPedigreesRequestParams) => Promise<{ data: Array<PedigreeListDto>; totalCount: number }>;
  selectedPedigree?: PedigreeListDto;
  onChange: (value?: PedigreeListDto) => void;
};

export const usePedigreeChooserController = (props: PedigreeChooserProps) => {
  const { 
    getAdminSmartListFunctions,
    savePedigreesColumns,
    getPedigreesColumns,
    getFolderFields,
    getFolders,
    selectedPedigree,
    showErrorMessage,
    getPedigrees,
    getFilters,
    onChange,
  } = props;

  const {
    filters,
    initializeFilters,
  } = useTableFiltersController({ getFilters });

  const itemUniqProperty = 'customPedigreeItemUniqProperty';

  const [adminFunctions, setAdminFunctions] = React.useState<AdminSmartListFunctionGetDto | undefined>();
  const [pedigreesCount, setPedigreesCount] = React.useState(0);
  const [pedigrees, setPedigrees] = React.useState<Array<PedigreeListDto>>([]);
  const [pedigreesLoading, setPedigreesLoading] = React.useState(false);
  const [pedigreesColumns, setPedigreesColumns] = React.useState<Array<ColumnListDto>>([]);
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [pedigreesLoadMetadata, setPedigreesLoadMetadata] = React.useState<NewTableLoadMetadata>({
    pagination: {
      size: 50,
      start: 0,
    },
  });

  const getAndSetPedigreesColumns = React.useCallback(async () => {
    const cols = await getPedigreesColumns();
    setPedigreesColumns(cols);

    return cols;
  }, [getPedigreesColumns]);

  const handleGetPedigrees = async (params: NewTableLoadMetadata & { columns: Array<ColumnListDto>; searchKeyword?: string }) => {
    try {
      setPedigreesLoading(true);

      const { filtering, pagination, sorting } = params;

      const columns = params.columns.map((x): ColumnListDto => {
        const isSorted = getColumnId(x) === sorting?.id;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : false,
        };
      });
  
      const filters = filtering?.columns || {};
      const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
        const columnIndex = pedigreesColumns.findIndex((x) => getColumnId(x) == columnId);
        const columnToRemoveBecauseOfActionsColumn = 1;
        const value = filters[columnId];
  
        return {
          columnId: columnIndex + columnToRemoveBecauseOfActionsColumn,
          operator1: value?.operator1 ?? null,
          operator2: value?.operator2 ?? null,
          value1: value?.whereClause1 ?? null,
          value2: value?.whereClause2 ?? null,
          andOr: value?.andOR,
        };
      });

      const { data, totalCount } = await getPedigrees({
        pagination,
        filtering: {
          search: params.searchKeyword,
          filterApplied: filtering?.filter ?? false,
          columnFilters: columnFiltersProcessed,
          columns,
        },
      });

      const processedData = data.map((x) => {
        (x as Record<string, any>)[itemUniqProperty] = generateId();

        return x;
      });

      const finalData = pagination.start > 0
        ? [...pedigrees, ...processedData]
        : processedData;

      setPedigrees(finalData);
      setPedigreesCount((x) => pagination.start > 0 ? x : totalCount);
      setPedigreesLoadMetadata({
        pagination,
        sorting,
        filtering
      });
      setSearchKeyword(params.searchKeyword || '');
    } finally {
      setPedigreesLoading(false);
    }
  };

  const initialize = async () => {
    try {
      getAdminSmartListFunctions()
        .then((res) => {
          setAdminFunctions(res);
        })
        .catch((err) => {
          console.error(err);
          const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get admin functions');
          showErrorMessage(errorMessage);
        });
  
      initializeFilters().catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get filters');
        showErrorMessage(errorMessage);
      });
  
      const pedigreesColumnsRes = await getAndSetPedigreesColumns();
  
      handleGetPedigrees({
        columns: pedigreesColumnsRes,
        pagination: {
          start: 0,
          size: 45,
        },
        sorting: undefined,
      }).catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get pedigrees');
        showErrorMessage(errorMessage);
      });
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get data');
      showErrorMessage(errorMessage);
    }
  };

  React.useEffect(() => {
    initialize();
  }, []);

  const getColumnId = React.useCallback((column: ColumnListDto) => {
    return `field_id__${column.field.id.toString()}__field_type__${column.field.type.toString()}__context__${column.field.context.toString()}`;
  }, []);

  const updatePedigreesColumns = React.useCallback(async (columns: Array<ColumnListDto>) => {
    await savePedigreesColumns(columns);
    setPedigreesColumns(columns);
  }, [savePedigreesColumns]);
  
  const getFoldersForAddingColumns = React.useCallback(() => {
    const pedigreesId = 6;

    return getFolders({ folderTypeId: pedigreesId, setUp: { rootFolderImgUrl: 'gif/df_pedigree.gif', rootFolderName: I18n.t('Pedigree Data Folders') } });
  }, [getFolders]);

  const getFolderFieldsForAddingColumns = React.useCallback((params: { searchQuery?: string; folderId?: FolderPrimaryListDto['id']; system: boolean }) => {
    const pedigreesContext = 2;
    
    return getFolderFields({
      ...params,
      contextId: pedigreesContext,
    });
  }, [getFolderFields]);

  const handleSelectedPedigreesChange = (selectedItems: Array<PedigreeListDto>) => {
    if (selectedItems.length) {
      onChange(selectedItems[0]!);
    }
  };

  const {
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    handleColumnsResizing,
    handleColumnsOrderChange
  } = useTableController({
    itemUniqProperty: itemUniqProperty, 
    columns: pedigreesColumns, 
    selectedItems: selectedPedigree ? [selectedPedigree] : [], 
    getFields: getFolderFieldsForAddingColumns, 
    getColumnId, 
    onColumnsResize: updatePedigreesColumns, 
    onColumnsOrderChange: updatePedigreesColumns, 
    onSelectedItemsChange: handleSelectedPedigreesChange,
  }, { withFirstActionsColumn: true });
  
  const renderPedigreeNameBodyCell = React.useCallback(({ row: { original } }: { row: { original: PedigreeListDto } }) => {
    return (
      <BodyNameCell
        item={original}
        label={original.name}
      />
    );
  }, []);

  const tableColumns = React.useMemo((): Array<NewTableColumn<PedigreeListDto>> => {
    const defaultWidth = 100;
    const nameCellId = 16;

    return pedigreesColumns.map((x) => ({
      id: getColumnId(x),
      accessorKey: x.field.id.toString(),
      cell: x.field.id === nameCellId ? renderPedigreeNameBodyCell : undefined,
      enableDragAndDrop: true,
      enableSorting: true,
      minSize: 20,
      size: x.width || defaultWidth,
      header: x.field.name,
      filter: { 
        type: x.field.type,
        options: x.field.options,
        outputDateFormat: 'YYYY-MM-DD'
      }
    }));
  }, [pedigreesColumns, getColumnId, renderPedigreeNameBodyCell]);


  type TableAction<T> = T & {
    condition?: boolean; 
    adminFunction?: AdminSmartListFunctionType;
  };

  const getColumnActions = () => {
    const baseList: Array<TableAction<NewTableColumnAction>> = [
      { type: NewTableColumnPredefinedActionType.filters, condition: Boolean(filters) },
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableColumnPredefinedActionType.addColumn, className: 'PedigreesTable__column-action-top-divider' },
      { adminFunction: AdminSmartListFunctionType.deleteColumn, type: NewTableColumnPredefinedActionType.deleteColumn },
      { type: NewTableColumnPredefinedActionType.clearSorting },
    ];

    return baseList.map((x) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : false;
      }

      return x;
    })
    .filter((x) => x.condition ?? true);
  };

  const getHeaderActions = () => {
    const baseList: Array<TableAction<NewTableHeaderAction>> = [
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableHeaderPredefinedActionType.addColumn, className: 'PedigreesTable__column-action-top-divider' },
    ]
    
    return baseList.map((x) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : false;
      }

      return x;
    })
    .filter((x) => x.condition ?? true);;
  };

  const headerActions = getHeaderActions();
  const columnActions = getColumnActions();

  const getHeaderElementId = React.useCallback(() => {
    return 'ped-home-tab-list-grid-thead-row0';
  }, []);

  const getHeaderCellElementId = React.useCallback((column: NewTableColumn<PedigreeListDto>, columnIndex: number) => {
    return `ped-home-tab-list-grid-thead-row0-cell${columnIndex}`;
  }, []);

  const getBodyRowElementId = React.useCallback((dataItem: PedigreeListDto, rowIndex: number) => {
    return `ped-home-tab-list-grid-tbody-row${rowIndex}`;
  }, []);

  const getBodyCellElementId = React.useCallback((column: NewTableColumn<PedigreeListDto>, columnIndex: number, dataItem: PedigreeListDto, rowIndex: number) => {
    return `ped-home-tab-list-grid-tbody-row${rowIndex}-cell${columnIndex}`;
  }, []);

  const getId = React.useMemo((): NewTableGetId => (
    {
      header: () => getTestAttributes({ id: "bw-quest-invites-step4-sel-ind-list-thead-row0" }).id,
      headerCell: (cell, index) => getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-thead-row0-cell${index}` }).id,
      bodyRow: (row, index) => getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-tbody-row${index}` }).id,
      bodyCell: (row, rowIndex, cell, cellIndex) => getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-tbody-row${rowIndex}-cell${cellIndex}` }).id,
    }
  ), []);

  return {
    searchKeyword,
    filters,
    pedigreesLoadMetadata,
    pedigreesCount,
    pedigrees,
    itemUniqProperty,
    pedigreesLoading,
    getId,
    changingColumns,
    handleColumnRemove,
    handleGetFields,
    handleColumnAdd,
    tableColumns,
    headerActions,
    columnActions,
    getPedigreesProcessed: (metadata: NewTableLoadMetadata) => {
      return handleGetPedigrees({ ...metadata, columns: pedigreesColumns, searchKeyword: searchKeyword });
    },
    handleSearch: (params: { searchKeyword: string }) => {
      return handleGetPedigrees({ ...pedigreesLoadMetadata, columns: pedigreesColumns, searchKeyword: params.searchKeyword });
    },
    getFoldersForAddingColumns,
    handleSelectedPedigreesChange,
    handleColumnsResizing,
    handleColumnsOrderChange,
    getHeaderElementId,
    getHeaderCellElementId,
    getBodyRowElementId,
    getBodyCellElementId,
  };
};